/**
 * メールソフト（Outlook / Thunderbird / iPhone など）の接続設定。
 *
 * ポートは ARCHITECTURE.md §5.2「本番メールノードが publish するポート」が唯一の正:
 *   25 / 587 / 465 / 143 / 993 / 110 / 995 / 4190
 * 可変なのは **ホスト名だけ** なので、それだけを API から受け取り、
 * 残りはここで組み立てる。画面ごとにポート番号を書かない。
 *
 * ログイン ID は必ず **メールアドレス全体**。ローカル部だけでは Dovecot が
 * ドメインを特定できず、別ドメインの同名アカウントと衝突する。
 */

export type Encryption = 'SSL/TLS' | 'STARTTLS' | 'なし';

export interface MailClientEntry {
  /** 画面に出す用途名。 */
  purpose: string;
  protocol: 'IMAP' | 'POP3' | 'SMTP' | 'ManageSieve';
  host: string;
  port: number;
  encryption: Encryption;
  /** 推奨設定かどうか。推奨でないものは「上級者向け」として畳んで出す。 */
  recommended: boolean;
  /** なぜこれを選ぶのかの平易な説明。 */
  note: string;
}

export interface MailClientSettings {
  host: string;
  username: string;
  entries: MailClientEntry[];
}

/**
 * @param host  API が返すメールサーバーのホスト名（`org.node.mailHostname`）
 * @param address メールアドレス。そのままログイン ID になる。
 */
export function buildMailClientSettings(host: string, address: string): MailClientSettings {
  return {
    host,
    username: address,
    entries: [
      {
        purpose: '受信（推奨）',
        protocol: 'IMAP',
        host,
        port: 993,
        encryption: 'SSL/TLS',
        recommended: true,
        note: 'メールをサーバーに置いたまま読みます。パソコンとスマートフォンの両方で同じ状態を見られます。',
      },
      {
        purpose: '送信（推奨）',
        protocol: 'SMTP',
        host,
        port: 465,
        encryption: 'SSL/TLS',
        recommended: true,
        note: '送信にもメールアドレスとパスワードの入力（SMTP 認証）が必要です。',
      },
      {
        purpose: '送信（STARTTLS）',
        protocol: 'SMTP',
        host,
        port: 587,
        encryption: 'STARTTLS',
        recommended: false,
        note: '465 番が使えないネットワークのときはこちらを使います。安全性は 465 番と同等です。',
      },
      {
        purpose: '受信（STARTTLS）',
        protocol: 'IMAP',
        host,
        port: 143,
        encryption: 'STARTTLS',
        recommended: false,
        note: '993 番が使えない場合のみ使用します。',
      },
      {
        purpose: '受信（POP）',
        protocol: 'POP3',
        host,
        port: 995,
        encryption: 'SSL/TLS',
        recommended: false,
        note: 'メールを端末にダウンロードして、サーバーから削除する方式です。1 台だけで使う場合に向きます。',
      },
      {
        purpose: '振り分けルールの管理',
        protocol: 'ManageSieve',
        host,
        port: 4190,
        encryption: 'STARTTLS',
        recommended: false,
        note: 'Thunderbird の Sieve 拡張などから、サーバー側の振り分けルールを編集するための設定です。',
      },
    ],
  };
}

/** 設定情報をテキストで書き出す（印刷・保存用）。 */
export function renderSettingsText(settings: MailClientSettings, brandName: string): string {
  const lines: string[] = [
    `${brandName} メール設定情報`,
    '',
    `メールアドレス / ユーザー名: ${settings.username}`,
    `メールサーバー: ${settings.host}`,
    '',
  ];
  for (const e of settings.entries) {
    lines.push(
      `【${e.purpose}】${e.protocol}  ホスト: ${e.host}  ポート: ${e.port}  暗号化: ${e.encryption}`,
    );
  }
  lines.push('', '※ パスワードはこの控えに含まれません。安全な場所に保管してください。');
  return lines.join('\n');
}
